import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";

import { RootState } from "../../../globals";
import HttpManager from "../../../services/HttpManager";
import { EquipoCanal, EquipoMensaje } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import { errorTexto, iniciales, tonoAvatar } from "./salesHelpers";
import { resumenTexto } from "./equipoGifs";
import { IconoCanal } from "./iconoCanalPicker";
import "./equipoReenviar.css";

/** Como WhatsApp: hasta 10 conversaciones a la vez. */
export const MAX_REENVIO = 10;

interface Props {
	campusId: number;
	/** El mensaje (o las fotos de un álbum, en orden) que se reenvía. */
	mensajes: EquipoMensaje[];
	/** Selección múltiple: salen como mensajes nuevos, sin "Reenviado" (Esteban, 2026-10-12). */
	nuevo?: boolean;
	onCerrar: () => void;
	/** Ya se mandó: las conversaciones a las que llegó (para recargar la abierta y la lista). */
	onEnviado: (canalIds: number[]) => void;
}

const sinAcentos = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const resumen = (lista: EquipoMensaje[]): string => {
	const m = lista[0], n = lista.length;
	if (n > 1) return n + (lista.every((x) => x.file_tipo && !x.body) ? " archivos" : " mensajes");
	const t = resumenTexto(m.body);
	if (t) return t;
	if (m.file_tipo === "audio") return "Nota de voz";
	if (m.file_tipo === "image") return "Foto";
	if (m.file_tipo === "video") return "Video";
	if (m.file_name) return m.file_name;
	return m.prospect_id ? "Cliente compartido" : "Mensaje";
};

/**
 * Reenviar (Esteban, 2026-10-12): elige conversaciones donde puedes escribir (canales, grupos, directos) y personas
 * (se abre o reusa el directo), hasta 10, y "Enviar". El back copia texto, archivo, GIF o cliente y lo marca "Reenviado".
 */
const EquipoReenviar: React.FC<Props> = ({ campusId, mensajes, nuevo, onCerrar, onEnviado }) => {
	const http = HttpManager.getInstance();
	const yoId: number | null = useSelector((state: RootState) => (state.rootReducer.sessionState as any).user?.id ?? null);
	const [canales, setCanales] = useState<EquipoCanal[] | null>(null);
	const [personas, setPersonas] = useState<{ id: number; nombre: string; rol: string | null; planteles?: string[] }[]>([]);
	const [q, setQ] = useState("");
	const [selCanales, setSelCanales] = useState<{ id: number; nombre: string }[]>([]);
	const [selPersonas, setSelPersonas] = useState<{ id: number; nombre: string }[]>([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		http.prospectsService.equipoCanales(campusId).then((r) => setCanales(r.data.canales)).catch((e) => { setCanales([]); setError(errorTexto(e)); });
	}, [campusId]);
	useEffect(() => {
		const t = setTimeout(() => http.prospectsService.equipoPersonas(campusId, q, null, null).then((r) => setPersonas(r.data.personas)).catch(() => setPersonas([])), 250);
		return () => clearTimeout(t);
	}, [q]);
	useEffect(() => {
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onCerrar(); };
		window.addEventListener("keydown", esc);
		return () => window.removeEventListener("keydown", esc);
	}, [onCerrar]);

	// Solo donde puedo escribir: en canales de anuncios, si soy administradora.
	const escribibles = useMemo(() => (canales || []).filter((c) => !c.anuncios || c.rol === "admin"), [canales]);
	const conDirecto = useMemo(() => new Set(escribibles.filter((c) => c.tipo === "directo" && c.otro_id).map((c) => c.otro_id as number)), [escribibles]);
	const filtro = sinAcentos(q.trim());
	const chats = filtro ? escribibles.filter((c) => sinAcentos(c.nombre).includes(filtro)) : escribibles;
	// Personas sin directo todavía (las que ya lo tienen salen arriba, en Chats).
	const gente = personas.filter((p) => p.id !== yoId && !conDirecto.has(p.id));
	const total = selCanales.length + selPersonas.length;
	const lleno = total >= MAX_REENVIO;

	const toggleCanal = (c: { id: number; nombre: string }) => setSelCanales((prev) => (prev.some((x) => x.id === c.id) ? prev.filter((x) => x.id !== c.id) : lleno ? prev : [...prev, c]));
	const togglePersona = (p: { id: number; nombre: string }) => setSelPersonas((prev) => (prev.some((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : lleno ? prev : [...prev, p]));

	const enviar = async () => {
		if (!total || busy) return;
		setBusy(true); setError(null);
		const llegaron = new Set<number>();
		const fallas: string[] = [];
		try {
			// Un álbum se reenvía foto por foto, en orden (llega como álbum).
			for (const m of mensajes) {
				const r = await http.prospectsService.equipoReenviar(campusId, m.id, selCanales.map((c) => c.id), selPersonas.map((p) => p.id), !!nuevo);
				Object.keys(r.data.ids || {}).forEach((k) => llegaron.add(Number(k)));
				(r.data.fallas || []).forEach((f) => {
					const quien = f.canalId ? selCanales.find((c) => c.id === f.canalId)?.nombre : selPersonas.find((p) => p.id === f.userId)?.nombre;
					const txt = (quien ? quien + ": " : "") + f.motivo;
					if (!fallas.includes(txt)) fallas.push(txt);
				});
			}
		} catch (e) {
			fallas.push(errorTexto(e));
		}
		setBusy(false);
		if (llegaron.size) onEnviado(Array.from(llegaron));
		if (fallas.length) {
			setError((llegaron.size ? "Se reenvió a " + llegaron.size + (llegaron.size === 1 ? " conversación" : " conversaciones") + ". No se pudo en: " : "") + fallas.join(" · "));
			setSelCanales((prev) => prev.filter((c) => !llegaron.has(c.id)));
			setSelPersonas([]);
			return;
		}
		onCerrar();
	};

	const avatar = (c: EquipoCanal) => (
		<span className={"eqr-av " + (c.tipo === "canal" ? "canal" : c.tipo === "grupo" ? "grupo" : tonoAvatar(c.id))} aria-hidden="true">
			{c.icono && c.tipo !== "directo" ? <IconoCanal id={c.id} icono={c.icono} /> : c.tipo === "canal" ? (c.anuncios ? <WappIcono n="megaphone" /> : "#") : c.tipo === "grupo" ? <WappIcono n="users" />
				: iniciales({ first_name: c.nombre.split(" ")[0], last_name: c.nombre.split(" ")[1] || "" })}
		</span>
	);
	const marca = (on: boolean) => <span className={"eqr-marca" + (on ? " on" : "")} aria-hidden="true">{on ? <WappIcono n="check" /> : null}</span>;
	const elegidos = [...selCanales, ...selPersonas];

	return createPortal(
		<div className="eqr-velo" onClick={onCerrar}>
			<div className="eqr" role="dialog" aria-modal="true" aria-label={mensajes.length > 1 ? "Reenviar Mensajes" : "Reenviar Mensaje"} onClick={(e) => e.stopPropagation()}>
				<div className="eqr-cab">
					<b>{mensajes.length > 1 ? "Reenviar " + mensajes.length + " Mensajes" : "Reenviar Mensaje"}</b>
					<button className="eqr-x" onClick={onCerrar} aria-label="Cerrar"><WappIcono n="x" /></button>
				</div>
				<div className="eqr-vista"><WappIcono n="forward" /><span>{mensajes[0] ? resumen(mensajes) : ""}</span></div>
				<input className="eqr-buscar" type="search" autoFocus placeholder="Buscar conversación o persona" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar conversación o persona" />
				<div className="eqr-lista">
					{canales === null ? <div className="eqr-vacio">Cargando…</div> : null}
					{chats.length ? <div className="eqr-sec">Chats</div> : null}
					{chats.map((c) => {
						const on = selCanales.some((x) => x.id === c.id);
						return (
							<button key={"c" + c.id} type="button" className={"eqr-fila" + (on ? " on" : "")} disabled={!on && lleno} aria-pressed={on} onClick={() => toggleCanal({ id: c.id, nombre: c.nombre })}>
								{avatar(c)}<span className="eqr-nom"><b>{c.nombre}</b>{c.tipo === "canal" ? <small>Canal</small> : c.tipo === "grupo" ? <small>Grupo</small> : null}</span>{marca(on)}
							</button>
						);
					})}
					{gente.length ? <div className="eqr-sec">Personas</div> : null}
					{gente.map((p) => {
						const on = selPersonas.some((x) => x.id === p.id);
						return (
							<button key={"p" + p.id} type="button" className={"eqr-fila" + (on ? " on" : "")} disabled={!on && lleno} aria-pressed={on} onClick={() => togglePersona({ id: p.id, nombre: p.nombre })}>
								<span className={"eqr-av " + tonoAvatar(p.id)} aria-hidden="true">{iniciales({ first_name: p.nombre.split(" ")[0], last_name: p.nombre.split(" ")[1] || "" })}</span>
								<span className="eqr-nom"><b>{p.nombre}</b>{p.rol ? <small>{p.rol}{p.planteles?.length ? " · " + p.planteles.join(", ") : ""}</small> : null}</span>{marca(on)}
							</button>
						);
					})}
					{canales !== null && !chats.length && !gente.length ? <div className="eqr-vacio">No hay resultados.</div> : null}
				</div>
				{error ? <div className="eqr-error" role="alert">{error}</div> : null}
				<div className="eqr-pie">
					<span className="eqr-elegidos">{elegidos.length ? elegidos.map((x) => x.nombre.split(" ")[0]).join(", ") : "Elige hasta " + MAX_REENVIO + " conversaciones"}
						{lleno ? <small> (máximo {MAX_REENVIO})</small> : null}</span>
					<button className="eqr-enviar" disabled={!total || busy} onClick={enviar}>{busy ? "Enviando…" : "Enviar"}{total && !busy ? " (" + total + ")" : ""}</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

export default EquipoReenviar;
